import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, EmptyState, Skeleton } from "@nexus/ui";
import { TASK_NAVIGATION_SECTION } from "@nexus/contracts";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useTenantSwitch } from "@/features/auth/hooks/useTenantSwitch";
import { tenantHasPage } from "@/features/auth/lib/assert-page-permission";
import {
  normalizedPreflightTenantId,
  preflightKeys,
  preflightReportQueryOptions,
  preflightReportStatusQueryOptions,
} from "@/lib/queries";
import { retryReport } from "../api/preflight.api";
import {
  isTerminalReportStatus,
  type PreflightReportAsset,
  type PreflightReportStatus,
  type PreflightScoreBand,
} from "../types/preflight.types";
import { formatAssetError } from "../lib/format-asset-error";
import { scoreToBand } from "../lib/score-band";
import { CreativeResultCard } from "../components/CreativeResultCard/CreativeResultCard";
import { ReportPageHeader } from "../components/ReportPageHeader/ReportPageHeader";
import { ReportInsights } from "../components/ReportInsights/ReportInsights";
import {
  CreativesToolbar,
  type CreativeSort,
} from "../components/CreativesToolbar/CreativesToolbar";
import { ReportBackLink } from "../components/ReportBackLink/ReportBackLink";
import { mediaKind, type MediaKind } from "../lib/media-type";

const PREFLIGHT_PAGE_SLUG = "pre-flight";

/** Sort weighting for "by type": videos first, then images, then everything else. */
const MEDIA_SORT_RANK: Record<MediaKind, number> = {
  video: 0,
  image: 1,
  other: 2,
};

/** Resolve a creative's band — from the server value, else derived from its score. */
function assetBand(asset: PreflightReportAsset): PreflightScoreBand | null {
  if (asset.band) return asset.band;
  if (asset.score == null) return null;
  return scoreToBand(asset.score, asset.hasUnanalyzed);
}

/**
 * Smoothly scrolls a creative's card into view (score-rail / heatmap → card jump) and parks
 * focus on it, so a keyboard jump continues from the card rather than the rail it came from.
 */
function scrollToAssetCard(assetId: string) {
  const el = document.getElementById(`preflight-asset-${assetId}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  el.focus({ preventScroll: true });
}

export interface PreFlightReportPageProps {
  reportId: string;
  /** Leave the report: back to the hub, or close the peek. Also runs after a delete. */
  onBack: () => void;
  /** Rendered inside the hub's side peek, whose own header replaces the back link. */
  isPeek?: boolean;
}

function LoadingState() {
  return (
    <div className="flex flex-col gap-4" aria-busy>
      <Skeleton className="h-32 w-full rounded-md" />
      <Skeleton className="h-64 w-full rounded-md" />
    </div>
  );
}

function failedReportDescription(
  assets: PreflightReportAsset[],
  t: TFunction,
): string {
  const failed = assets.find((asset) => asset.status === "FAILED");
  if (!failed?.error) {
    return t(
      "preflight.report.failedDescription",
      "Something went wrong while scoring this report. You can re-run the analysis.",
    );
  }
  return t(
    "preflight.report.failedDescriptionWithReason",
    "{{reason}} You can re-run the analysis.",
    { reason: formatAssetError(failed.error, t) },
  );
}

/**
 * Full-page Report view (report-ui R1–R12). Renders the summary + per-creative breakdown
 * over the server-projected features; polls while non-terminal and flips in place, and
 * renders dedicated failed / not-found states so a deep link never crashes (KTD7).
 */
export function PreFlightReportPage({
  reportId,
  onBack,
  isPeek = false,
}: PreFlightReportPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const tenants = useAuth((s) => s.tenants);
  const switchTenant = useTenantSwitch();
  const tenantKey = normalizedPreflightTenantId(activeTenantId);
  const reportQuery = useQuery(
    preflightReportQueryOptions(activeTenantId, reportId),
  );
  const report = reportQuery.data;
  const reloadMedia = () => void reportQuery.refetch();

  // Align once per report: the API cross-resolves the owning tenant for admins, so
  // re-running after a manual switch would snap the user straight back.
  const alignedReportIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!report || alignedReportIdRef.current === report.id) return;
    if (report.tenantId === activeTenantId) {
      alignedReportIdRef.current = report.id;
      return;
    }
    // Not a member, or the stored membership list hasn't caught up yet — retry
    // when it changes instead of marking the report aligned without switching.
    const target = tenants.find((tenant) => tenant.id === report.tenantId);
    if (!target) return;
    alignedReportIdRef.current = report.id;
    // Switching re-runs the route guard; an org without the page would turn a
    // readable report into a PageError.
    if (!tenantHasPage(target, PREFLIGHT_PAGE_SLUG)) return;
    switchTenant(report.tenantId);
  }, [report, activeTenantId, tenants, switchTenant]);

  const reportIsNonTerminal =
    report != null && !isTerminalReportStatus(report.status);
  const pollStartedAtRef = useRef(0);
  if (reportIsNonTerminal && pollStartedAtRef.current === 0) {
    pollStartedAtRef.current = Date.now();
  }

  const statusQuery = useQuery(
    preflightReportStatusQueryOptions(
      activeTenantId,
      reportId,
      pollStartedAtRef.current,
    ),
  );
  const liveStatus = statusQuery.data?.status;

  useEffect(() => {
    if (liveStatus && isTerminalReportStatus(liveStatus)) {
      void queryClient.invalidateQueries({
        queryKey: preflightKeys.detail(tenantKey, reportId),
      });
    }
  }, [liveStatus, queryClient, reportId, tenantKey]);

  const retryMutation = useMutation({
    mutationFn: (assetId?: string) => retryReport(reportId, assetId),
    onSuccess: () => {
      pollStartedAtRef.current = 0;
      void queryClient.invalidateQueries({
        queryKey: preflightKeys.detail(tenantKey, reportId),
      });
      toast.success(t("preflight.toast.rerunning"));
    },
    onError: () => toast.error(t("preflight.toast.retryError")),
    meta: { skipGlobalError: true },
  });

  // Heatmap cell → jump to a specific criterion row in a creative's card. A fresh object per
  // click (even on the same cell) lets the target card re-run its scroll/flash effect.
  const [criterionFocus, setCriterionFocus] = useState<{
    assetId: string;
    key: string;
  } | null>(null);
  const handleSelectCriterion = useCallback((assetId: string, key: string) => {
    // The target card opens its breakdown, scrolls the row into view and flash-highlights it.
    setCriterionFocus({ assetId, key });
  }, []);

  // Score-rail dot → jump to the whole card: the page scrolls, the card outlines itself and
  // expands. A fresh object per click retriggers it for the same creative.
  const [assetReveal, setAssetReveal] = useState<{ assetId: string } | null>(
    null,
  );
  const handleSelectAsset = useCallback((assetId: string) => {
    scrollToAssetCard(assetId);
    setAssetReveal({ assetId });
  }, []);

  // Creative-list controls: sort order + band/media filters + bulk expand/collapse.
  const [sort, setSort] = useState<CreativeSort>("default");
  const [bandFilter, setBandFilter] = useState<PreflightScoreBand[]>([]);
  const [mediaFilter, setMediaFilter] = useState<MediaKind[]>([]);
  const [allExpanded, setAllExpanded] = useState(false);
  const [collapseSignal, setCollapseSignal] = useState<{
    open: boolean;
  } | null>(null);
  const handleToggleExpandAll = useCallback(() => {
    const next = !allExpanded;
    setAllExpanded(next);
    // Fresh object each click so every card re-applies the bulk state.
    setCollapseSignal({ open: next });
  }, [allExpanded]);

  const sortedAssets = useMemo(() => {
    const assets = report?.assets ?? [];
    if (sort === "default") return assets;
    if (sort === "type") {
      // Group video → image → other; Array.sort is stable so within-group order holds.
      return [...assets].sort(
        (a, b) =>
          MEDIA_SORT_RANK[mediaKind(a.contentType)] -
          MEDIA_SORT_RANK[mediaKind(b.contentType)],
      );
    }
    const direction = sort === "score-desc" ? -1 : 1;
    return [...assets].sort((a, b) => {
      // Unscored creatives (pending/failed) always sink to the bottom.
      if (a.score == null && b.score == null) return 0;
      if (a.score == null) return 1;
      if (b.score == null) return -1;
      return (a.score - b.score) * direction;
    });
  }, [report?.assets, sort]);

  // Creatives with no filename are named after their place in the report, so the number has
  // to come from the report order rather than the sorted/filtered list on screen.
  const assetPositions = useMemo(
    () =>
      new Map(
        (report?.assets ?? []).map((asset, index) => [asset.id, index + 1]),
      ),
    [report?.assets],
  );

  const bandCounts = useMemo(() => {
    const counts: Record<PreflightScoreBand, number> = {
      STRONG: 0,
      NEEDS_REVIEW: 0,
      AT_RISK: 0,
    };
    for (const asset of report?.assets ?? []) {
      const band = assetBand(asset);
      if (band) counts[band] += 1;
    }
    return counts;
  }, [report?.assets]);

  const mediaCounts = useMemo(() => {
    const counts: Record<MediaKind, number> = { video: 0, image: 0, other: 0 };
    for (const asset of report?.assets ?? []) {
      counts[mediaKind(asset.contentType)] += 1;
    }
    return counts;
  }, [report?.assets]);

  // Band + media filters narrow the list; an empty selection means "all". Unscored
  // creatives (no band) drop out whenever a band filter is active.
  const visibleAssets = useMemo(() => {
    return sortedAssets.filter((asset) => {
      if (bandFilter.length > 0) {
        const band = assetBand(asset);
        if (band == null || !bandFilter.includes(band)) return false;
      }
      if (
        mediaFilter.length > 0 &&
        !mediaFilter.includes(mediaKind(asset.contentType))
      ) {
        return false;
      }
      return true;
    });
  }, [sortedAssets, bandFilter, mediaFilter]);

  const status: PreflightReportStatus | undefined = report
    ? (liveStatus ?? report.status)
    : undefined;
  const inProgress = status != null && !isTerminalReportStatus(status);
  const isTerminalNonFailed =
    status != null && !inProgress && status !== "FAILED";
  const canRetryFailedAssets =
    status === "COMPLETED_WITH_ERRORS" && !inProgress;

  const backLink = isPeek ? null : <ReportBackLink onBack={onBack} />;

  if (reportQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {backLink}
        <LoadingState />
      </div>
    );
  }

  if (reportQuery.isError || !report) {
    return (
      <div className="flex flex-col gap-4">
        {backLink}
        <EmptyState
          variant="error"
          title={t("preflight.report.notFoundTitle")}
          description={t("preflight.report.notFoundDescription")}
          action={
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={onBack}
            >
              {t("preflight.report.backToHub")}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {status === "FAILED" ? (
        <>
          {backLink}
          <EmptyState
            variant="error"
            title={t("preflight.report.failedTitle")}
            description={failedReportDescription(report.assets, t)}
            action={
              <Button
                type="button"
                variant="secondary"
                size="sm"
                iconStart="refresh-cw"
                disabled={retryMutation.isPending}
                onClick={() => retryMutation.mutate(undefined)}
              >
                {t("preflight.report.retryAnalysis")}
              </Button>
            }
          />
          {report.assets.map((asset, index) => (
            <CreativeResultCard
              key={asset.id}
              asset={asset}
              position={index + 1}
              report={report}
              onReloadMedia={reloadMedia}
              reloadMediaPending={reportQuery.isFetching}
            />
          ))}
        </>
      ) : (
        <div className="flex flex-col gap-6">
          <ReportPageHeader
            report={report}
            showExport={isTerminalNonFailed}
            showBackLink={!isPeek}
            onBack={onBack}
            onDeleted={onBack}
          />

          {inProgress ? null : (
            <ReportInsights
              report={report}
              onSelectAsset={handleSelectAsset}
              onSelectCriterion={handleSelectCriterion}
            />
          )}

          <section
            className="scroll-mt-6 flex flex-col gap-3"
            aria-labelledby="preflight-creatives-heading"
            data-task-section={TASK_NAVIGATION_SECTION.PREFLIGHT_CREATIVES}
          >
            <div className="flex items-center justify-between gap-3">
              <h2
                id="preflight-creatives-heading"
                className="font-mono text-[11px] font-medium tracking-wide text-muted-foreground uppercase"
              >
                {t("preflight.report.creativesSection")}
              </h2>
              {!inProgress && report.assets.length > 1 ? (
                <CreativesToolbar
                  sort={sort}
                  onSortChange={setSort}
                  bandFilter={bandFilter}
                  onBandFilterChange={setBandFilter}
                  bandCounts={bandCounts}
                  mediaFilter={mediaFilter}
                  onMediaFilterChange={setMediaFilter}
                  mediaCounts={mediaCounts}
                  allExpanded={allExpanded}
                  onToggleExpandAll={handleToggleExpandAll}
                />
              ) : null}
            </div>
            {visibleAssets.map((asset) => (
              <div
                key={asset.id}
                id={`preflight-asset-${asset.id}`}
                // Focusable only as a scroll target — never in the tab order.
                tabIndex={-1}
                className="scroll-mt-6 outline-none"
              >
                <CreativeResultCard
                  asset={asset}
                  position={assetPositions.get(asset.id)}
                  report={report}
                  exportable={isTerminalNonFailed}
                  showRetry={canRetryFailedAssets && asset.status === "FAILED"}
                  retryPending={retryMutation.isPending}
                  focus={
                    criterionFocus?.assetId === asset.id ? criterionFocus : null
                  }
                  reveal={
                    assetReveal?.assetId === asset.id ? assetReveal : null
                  }
                  collapseSignal={collapseSignal}
                  onRetry={() => retryMutation.mutate(asset.id)}
                  onReloadMedia={reloadMedia}
                  reloadMediaPending={reportQuery.isFetching}
                />
              </div>
            ))}
          </section>
        </div>
      )}
    </div>
  );
}
